
import { WidgetProps } from '@highimpact-front/remote-widget-core';
import {
  ApiDashboardElement,
  ApiReturnCampaign,
} from '@highimpact-frontend/api-types';
import type { DisplayerTypeSuccess } from '@highimpact-frontend/displayer-types';
import type { DataState, MeasureType } from '@highimpact/dashboard-types';
import type { Config, LayoutAxis } from 'plotly.js-dist-min';

export interface PlotProps extends WidgetProps {
  className?: string;

  is_editable: boolean;

  mode?: 'line' | 'hist';

  gridId: number;

  displayer?: DisplayerTypeSuccess;

  elementData: ApiDashboardElement;

  dataset?: DataState<number, MeasureType>;

  campaigns: ApiReturnCampaign[];
}

/** Plot props for timeseries/scatter where the hook loads data without a Redux dataset. */
export type ScatterPlotProps = Omit<PlotProps, 'dataset' | 'displayer'> & {
  dataset?: DataState<number, MeasureType>;
  displayer?: DisplayerTypeSuccess;
};

export type AxisLayoutConfigEntry = {
  key: string;
  unit: string;
  layout: Partial<LayoutAxis>;
};

/**
 * Maps Plotly figure layout axis ranges back to displayer axis ids.
 *
 * Plotly names the first y-axis `yaxis` (no suffix) even when our layout key is
 * `yaxis${id}`; this helper resolves that mismatch using the axis config map.
 */
export function readAxisRangesFromPlotlyFigure(
  figureLayout: Record<string, Partial<LayoutAxis>>,
  axisConfig: Record<string | number, AxisLayoutConfigEntry>
): Array<{
  axis_id: number;
  range: [string | number | null, string | number | null];
}> {
  const entries = Object.entries(axisConfig);

  return entries.flatMap(([axisIdStr, { key }]) => {
    const axis_id = Number(axisIdStr);
    if (!Number.isFinite(axis_id)) {
      return [];
    }

    const axisPrefix = key.startsWith('xaxis')
      ? 'xaxis'
      : key.startsWith('yaxis')
      ? 'yaxis'
      : null;
    if (!axisPrefix) {
      return [];
    }

    const sameKindCount = entries.filter(([, entry]) =>
      entry.key.startsWith(axisPrefix)
    ).length;

    const plotlyAxisLayout =
      figureLayout[key] ??
      (sameKindCount === 1 ? figureLayout[axisPrefix] : undefined);

    const range = plotlyAxisLayout?.range;
    if (!range || range.length < 2) {
      return [];
    }

    return [{ axis_id, range: [range[0], range[1]] }];
  });
}

export function getDefaultConfig(name: string): Partial<Config> {
  return {
    editable: false,
    responsive: true,
    toImageButtonOptions: {
      format: 'png' as Config['toImageButtonOptions']['format'],
      filename: name,
      height: 500,
      width: 700,
      scale: 1,
    },
    locale: 'fr',
    displaylogo: false,
  };
}
